import React, { useState, useEffect } from 'react';
import {
  Table, Button, Tag, Tooltip, Input, Modal, message, Typography, Badge, Empty, Result,
  Space, Dropdown, Card, Row, Col, Segmented, Select, Spin, Collapse,
} from 'antd';
import { SearchOutlined, ReloadOutlined, CopyOutlined, ShopOutlined, ArrowRightOutlined, ArrowLeftOutlined, InfoCircleOutlined, ThunderboltOutlined, SaveOutlined, StopOutlined, GlobalOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getRecTransmisiones,
  getRecTransmisionById,
  updateRecTransmisionEstado,
  procesarTransmision,
  getFuentesOficialesCobertura,
  ESTADO_PENDIENTE,
  ESTADO_REALIZADO,
  ESTADO_DESCARTADO,
  INCIDENCIA_TOOLTIPS,
  getIncidencias,
  type ReconciliacionTransmision,
  type ReconciliacionIncidencia,
  type ProcesarTransmisionResponse,
  getRecTitularesFarmaciaGescom,
} from '../../api/reconciliacionApi';
import { fct } from '../../fct/tokens/tokens';
import { FctEstado, FctGlifoAtencion, claseFilaAtencion, nivelDesdeIncidencias } from '../../fct/react';
import FarmaciaTitularesModal from '../FarmaciaTitularesModal';
import FuenteOficialModal from './FuenteOficialModal';
import AbrirCasoButton from '../MejoraCU2/AbrirCasoButton';
import { IncidenciaCodigo, EstadoOperacion, TipoOperacion, ResultadoIcono } from './semantica';

const { Text } = Typography;
const { TextArea } = Input;

type IncidenciaFilter = 'todos' | 'sin' | 'con' | 'e4';

interface TransmisionesTabProps {
  selectedRecIds?: number[];
}

const TransmisionesTab: React.FC<TransmisionesTabProps> = ({ selectedRecIds }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState<ReconciliacionTransmision[]>([]);
  const [filteredData, setFilteredData] = useState<ReconciliacionTransmision[]>([]);
  const ESTADO_TODOS = -1;
  const [selectedEstado, setSelectedEstado] = useState<number>(ESTADO_PENDIENTE);
  const [incidenciaFilter, setIncidenciaFilter] = useState<IncidenciaFilter>('todos');
  const PROVINCIA_TODAS = '__todas__';
  const [provinciaFilter, setProvinciaFilter] = useState<string>(PROVINCIA_TODAS);
  const [searchText, setSearchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [processing, setProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{
    total: number;
    current: number;
    currentName: string;
    success: number;
    failed: number;
    cancelled: boolean;
    done: boolean;
  } | null>(null);
  const [batchProcessingId, setBatchProcessingId] = useState<number | null>(null);
  const cancelBatchRef = React.useRef(false);
  const [estadoCounts, setEstadoCounts] = useState({ pendiente: 0, realizada: 0, descartada: 0 });
  const [loadingCounts, setLoadingCounts] = useState(false);

  // Cambio de estado por lotes (descartar pendientes / revertir descartadas)
  const [batchEstado, setBatchEstado] = useState<number | undefined>(undefined);
  const [batchEstadoLoading, setBatchEstadoLoading] = useState(false);

  // Modal detalle
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ReconciliacionTransmision | null>(null);
  const [editNotas, setEditNotas] = useState('');
  const [editEstadoId, setEditEstadoId] = useState<number>(ESTADO_PENDIENTE);
  const [showTitularesModal, setShowTitularesModal] = useState(false);
  const [showFuenteOficialModal, setShowFuenteOficialModal] = useState(false);
  const [coberturaFuentes, setCoberturaFuentes] = useState<string[]>([]);
  const [relatedIncidencias, setRelatedIncidencias] = useState<ReconciliacionIncidencia[]>([]);
  const [procesando, setProcesando] = useState(false);
  const [procesarResult, setProcesarResult] = useState<ProcesarTransmisionResponse | null>(null);

  const fetchByEstado = async (estadoId: number) => {
    const apiEstado = estadoId === ESTADO_TODOS ? undefined : estadoId;
    if (!selectedRecIds || selectedRecIds.length === 0) return getRecTransmisiones(undefined, apiEstado);
    const results = await Promise.all(selectedRecIds.map(rid => getRecTransmisiones(rid, apiEstado)));
    return results.flat();
  };

  const loadCounts = async () => {
    setLoadingCounts(true);
    try {
      const [pendientes, realizadas, descartadas] = await Promise.all([
        fetchByEstado(ESTADO_PENDIENTE),
        fetchByEstado(ESTADO_REALIZADO),
        fetchByEstado(ESTADO_DESCARTADO),
      ]);
      setEstadoCounts({ pendiente: pendientes.length, realizada: realizadas.length, descartada: descartadas.length });
    } catch {
      // silently fail
    } finally {
      setLoadingCounts(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await fetchByEstado(selectedEstado);
      setData(result);
      applyFilters(result, searchText, incidenciaFilter);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // Cobertura de fuentes oficiales (GDO-T40): una sola vez al montar
  useEffect(() => {
    getFuentesOficialesCobertura().then(setCoberturaFuentes).catch(() => setCoberturaFuentes([]));
  }, []);

  // Comparación tolerante a variantes de grafía ("A Coruña" / "La Coruña" / "Coruña (A)")
  const normProvincia = (p?: string) =>
    (p || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toUpperCase()
      .replace(/[^A-Z ]/g, ' ')
      .split(/\s+/)
      .filter(t => t.length > 2 && !['LAS', 'LOS'].includes(t))
      .join(' ');

  const tieneCoberturaFuente = (provincia?: string) =>
    !!provincia && coberturaFuentes.some(c => normProvincia(c) === normProvincia(provincia));

  useEffect(() => {
    loadData();
    setSelectedRowKeys([]);
  }, [selectedEstado, selectedRecIds]);

  useEffect(() => {
    loadCounts();
  }, [selectedRecIds]);

  const hasE4 = (t: ReconciliacionTransmision) =>
    t.vendedores?.some(v => v.resultado === -1 && v.resultadoNotas?.startsWith('[E4]')) ||
    t.compradores?.some(c => c.resultado === -1 && c.resultadoNotas?.startsWith('[E4]'));

  // Códigos de incidencia/bloqueo activos en la fila (para el glifo, el estado bloqueado y el filete de fila)
  const codigosFila = (t: ReconciliacionTransmision): string[] => {
    const codigos: string[] = [];
    if (t.vendedores?.some(v => v.excepcionE2)) codigos.push('E2');
    if (t.compradores?.some(c => c.excepcionE1)) codigos.push('E1');
    if (t.tieneIncidenciaI1) codigos.push('I1');
    if (t.tieneIncidenciaE3) codigos.push('E3');
    [...(t.vendedores || []), ...(t.compradores || [])].forEach(x => {
      const m = x.resultado === -1 ? x.resultadoNotas?.match(/^\[([A-Z0-9]+)\]/) : null;
      if (m && !codigos.includes(m[1])) codigos.push(m[1]);
    });
    return codigos;
  };

  const applyFilters = (
    items: ReconciliacionTransmision[],
    text: string,
    incFilter: IncidenciaFilter,
    provFilter: string = provinciaFilter,
  ) => {
    let result = items;

    // Filtro incidencia
    if (incFilter === 'sin') result = result.filter(t => !t.tieneExcepciones);
    else if (incFilter === 'con') result = result.filter(t => t.tieneExcepciones);
    else if (incFilter === 'e4') result = result.filter(hasE4);

    // Filtro provincia
    if (provFilter !== PROVINCIA_TODAS) {
      result = result.filter(t => (t.farmacia?.provincia || '') === provFilter);
    }

    // Filtro búsqueda
    if (text.trim()) {
      const lower = text.toLowerCase();
      result = result.filter(t =>
        (t.farmacia?._Name || '').toLowerCase().includes(lower) ||
        t.patron.toLowerCase().includes(lower) ||
        (t.notasGestion || '').toLowerCase().includes(lower)
      );
    }
    setFilteredData(result);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    applyFilters(data, value, incidenciaFilter);
  };

  const handleIncidenciaChange = (value: IncidenciaFilter) => {
    setIncidenciaFilter(value);
    setSelectedRowKeys([]);
    setBatchEstado(undefined);
    applyFilters(data, searchText, value, provinciaFilter);
  };

  const handleProvinciaChange = (value: string) => {
    setProvinciaFilter(value);
    setSelectedRowKeys([]);
    setBatchEstado(undefined);
    applyFilters(data, searchText, incidenciaFilter, value);
  };

  // Provincias disponibles en los datos cargados (para el desplegable de filtro)
  const provinciaOptions = React.useMemo(() => {
    const provincias = Array.from(
      new Set(data.map(t => t.farmacia?.provincia).filter((p): p is string => !!p))
    ).sort((a, b) => a.localeCompare(b, 'es'));
    return [
      { label: 'Todas las provincias', value: PROVINCIA_TODAS },
      ...provincias.map(p => ({ label: p, value: p })),
    ];
  }, [data]);

  const sinIncidenciaPendientes = data.filter(t => t.estadoId === ESTADO_PENDIENTE && !t.tieneExcepciones);

  const processBatch = async (items: ReconciliacionTransmision[], forzarTitularidad = false) => {
    setProcessing(true);
    cancelBatchRef.current = false;
    setBatchProgress({ total: items.length, current: 0, currentName: '', success: 0, failed: 0, cancelled: false, done: false });

    let success = 0;
    let failed = 0;

    for (let i = 0; i < items.length; i++) {
      if (cancelBatchRef.current) {
        setBatchProgress(prev => prev ? { ...prev, cancelled: true, done: true } : null);
        break;
      }

      const item = items[i];
      const farmaciaName = item.farmacia?._Name || `ID ${item.farmaciaId}`;
      setBatchProcessingId(item.id);
      setBatchProgress(prev => prev ? { ...prev, current: i + 0.5, currentName: farmaciaName } : null);

      try {
        const result = await procesarTransmision(item.id, forzarTitularidad);
        if (result.error !== 0) {
          failed++;
        } else {
          success++;
        }
        try {
          const refreshed = await getRecTransmisionById(item.id);
          setData(prev => prev.map(t => t.id === item.id ? refreshed : t));
          setFilteredData(prev => prev.map(t => t.id === item.id ? refreshed : t));
        } catch { /* keep going */ }
      } catch {
        failed++;
      }

      setBatchProgress(prev => prev ? { ...prev, current: i + 1, success, failed } : null);
    }

    setBatchProcessingId(null);
    setBatchProgress(prev => prev ? { ...prev, done: true, success, failed } : null);
    setProcessing(false);
    setSelectedRowKeys([]);
    loadCounts();
  };

  const handleCancelBatch = () => {
    cancelBatchRef.current = true;
  };

  const handleCloseBatchProgress = () => {
    setBatchProgress(null);
  };

  const handleProcesarTodos = () => {
    const items = sinIncidenciaPendientes;
    Modal.confirm({
      title: 'Actualizar Gescom',
      content: `Se actualizarán las titularidades de ${items.length} transmisiones sin bloqueo. Esta acción no se puede deshacer. ¿Continuar?`,
      okText: 'Aceptar',
      cancelText: 'Cancelar',
      onOk: () => { processBatch(items); },
    });
  };

  const handleProcesarSeleccionados = () => {
    const items = data.filter(t => selectedRowKeys.includes(t.id));
    Modal.confirm({
      title: 'Actualizar Gescom',
      content: `Se actualizarán las titularidades de ${items.length} transmisiones seleccionadas. ¿Continuar?`,
      okText: 'Aceptar',
      cancelText: 'Cancelar',
      onOk: () => { processBatch(items); },
    });
  };

  const handleProcesarE4Seleccionados = () => {
    const items = data.filter(t => selectedRowKeys.includes(t.id));
    Modal.confirm({
      title: 'Procesar con bloqueo E4',
      content: `Se procesarán ${items.length} transmisiones forzando la titularidad. La titularidad previa de los titulares entrantes se dará por finalizada. ¿Continuar?`,
      okText: 'Aceptar',
      cancelText: 'Cancelar',
      onOk: () => { processBatch(items, true); },
    });
  };

  const doBatchEstado = async () => {
    setBatchEstadoLoading(true);
    try {
      await Promise.all(selectedRowKeys.map(id => updateRecTransmisionEstado(Number(id), batchEstado!)));
      message.success(`${selectedRowKeys.length} transmisiones actualizadas`);
      setSelectedRowKeys([]);
      setBatchEstado(undefined);
      await Promise.all([loadData(), loadCounts()]);
    } catch {
      message.error('Error al actualizar las transmisiones en lote');
    } finally {
      setBatchEstadoLoading(false);
    }
  };

  const handleBatchEstado = () => {
    if (batchEstado === undefined || selectedRowKeys.length === 0) return;
    const label = selectedEstado === ESTADO_PENDIENTE ? 'Descartada' : 'Pendiente';
    Modal.confirm({
      title: 'Confirmar cambio de estado',
      content: `Se marcarán ${selectedRowKeys.length} transmisiones como "${label}". ¿Continuar?`,
      okText: 'Aceptar',
      cancelText: 'Cancelar',
      onOk: doBatchEstado,
    });
  };

  const handleUpdateEstado = async (id: number, estadoId: number, notasGestion?: string) => {
    try {
      await updateRecTransmisionEstado(id, estadoId, notasGestion);
      message.success('Transmisión actualizada correctamente');
      setModalVisible(false);
      loadData();
      loadCounts();
    } catch {
      message.error('Error al actualizar la transmisión');
    }
  };

  const openDetail = async (item: ReconciliacionTransmision) => {
    setSelectedItem(item);
    setEditNotas(item.notasGestion || '');
    setEditEstadoId(item.estadoId);
    setRelatedIncidencias([]);
    setProcesarResult(null);
    setModalVisible(true);

    if (item.tieneExcepciones) {
      try {
        const incidencias = await getIncidencias(item.reconciliacionId);
        const related = incidencias.filter(i =>
          i.farmaciaId === item.farmaciaId && (i.tipo === 'E1' || i.tipo === 'E2' || i.tipo === 'I1')
        );
        setRelatedIncidencias(related);
      } catch {
        // silently fail
      }
    }
  };

  const columns: ColumnsType<ReconciliacionTransmision> = [
    {
      title: 'Rec',
      dataIndex: 'reconciliacionId',
      key: 'rec',
      className: 'fct-num',
      align: 'right',
      width: 45,
      render: (val: number) => <Text type="secondary" style={{ fontSize: 12 }}>{val}</Text>,
    },
    {
      title: 'Farmacia',
      key: 'farmacia',
      render: (_, r) => r.farmacia?._Name || `ID ${r.farmaciaId}`,
      sorter: (a, b) => (a.farmacia?._Name || '').localeCompare(b.farmacia?._Name || ''),
      ellipsis: true,
      width: 280,
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      className: 'fct-num',
      align: 'right',
      width: 110,
      render: (val: string) => val ? new Date(val).toLocaleDateString('es-ES') : '-',
    },
    {
      title: <Tooltip overlayStyle={{ maxWidth: 500 }} title={<div style={{ whiteSpace: 'pre-line' }}>{'AT = Alta Titular, CT = Cierre Titular\n───────────────────\nF2 = entra algún titular (AT>0), no sale ninguno (CT=0)\nF3 = no entra ningún titular (AT=0), sale alguno (CT>0)\nF4 = hay entrada y salida de titulares (AT>0, CT>0)'}</div>}>Patrón</Tooltip>,
      dataIndex: 'patron',
      key: 'patron',
      width: 80,
      render: (val: string) => {
        const desc: Record<string, string> = {
          F2: 'Entra algún titular, no sale ninguno',
          F3: 'No entra ningún titular, sale alguno',
          F4: 'Hay entrada y salida de titulares',
        };
        return <Tooltip title={desc[val] || val}><Tag>{val}</Tag></Tooltip>;
      },
    },
    {
      title: <Tooltip title="Nº de titulares activos de la farmacia en Gescom. 0 = farmacia sin titular.">Titulares</Tooltip>,
      dataIndex: 'titularesGescom',
      key: 'titularesGescom',
      width: 90,
      className: 'fct-num',
      align: 'right',
      sorter: (a, b) => a.titularesGescom - b.titularesGescom,
      filters: [
        { text: 'Sin titular (0)', value: 0 },
        { text: 'Con titular (≥1)', value: 1 },
      ],
      onFilter: (value, record) => value === 0 ? record.titularesGescom === 0 : record.titularesGescom > 0,
      render: (val: number) => val === 0
        ? <Tooltip title="La farmacia no tiene titulares activos en Gescom"><span><FctGlifoAtencion nivel="atencion" /> <Text>0</Text></span></Tooltip>
        : <Text>{val}</Text>,
    },
    {
      title: <Tooltip title="Cierre (baja) de titular">CT</Tooltip>,
      key: 'vendedores',
      width: 50,
      className: 'fct-num',
      align: 'right',
      render: (_, r) => r.vendedores?.length || 0,
    },
    {
      title: <Tooltip title="Alta de titular">AT</Tooltip>,
      key: 'compradores',
      width: 50,
      className: 'fct-num',
      align: 'right',
      render: (_, r) => r.compradores?.length || 0,
    },
    {
      title: <Tooltip overlayStyle={{ maxWidth: 650 }} title={<div style={{ whiteSpace: 'pre-line' }}>{'Bloqueos que requieren supervisión\n───────────────────\nE1 = Alta de titularidad bloqueada porque su finalización se ha verificado recientemente\nE2 = Cierre de titularidad bloqueado porque la titularidad se ha verificado recientemente\nI1 = La farmacia quedaría sin titular con estos cambios\n───────────────────\nNormalmente se trata de transmisiones notificadas por los asesores y/o verificadas por otros medios'}</div>}>Bloqueos</Tooltip>,
      key: 'bloqueos',
      width: 70,
      render: (_, r) => {
        const tags: React.ReactNode[] = [];
        // E1/E2 del motor
        r.vendedores?.forEach(v => {
          if (v.excepcionE2) tags.push(<IncidenciaCodigo key={`E2-${v.vendedorId}`} codigo="E2" tooltip={INCIDENCIA_TOOLTIPS.E2} />);
        });
        r.compradores?.forEach(c => {
          if (c.excepcionE1) tags.push(<IncidenciaCodigo key={`E1-${c.compradorId}`} codigo="E1" tooltip={INCIDENCIA_TOOLTIPS.E1} />);
        });
        // I1 (invariante)
        if (r.tieneIncidenciaI1) tags.push(<IncidenciaCodigo key="I1" codigo="I1" tooltip={INCIDENCIA_TOOLTIPS.I1} />);
        const tiposVistos = new Set<string>();
        // E3 registrado por el motor (farmacia con operación abierta)
        if (r.tieneIncidenciaE3) {
          tiposVistos.add('E3');
          tags.push(<IncidenciaCodigo key="E3" codigo="E3" tooltip={r.descripcionE3 || 'La farmacia tiene una operación abierta'} />);
        }
        // Bloqueos de procesamiento (E3, E4, etc.) — extraer tipo del prefijo [XX] en resultadoNotas
        const procesados = [
          ...(r.vendedores?.filter(v => v.resultado === -1 && v.resultadoNotas) || []).map(v => v.resultadoNotas!),
          ...(r.compradores?.filter(c => c.resultado === -1 && c.resultadoNotas) || []).map(c => c.resultadoNotas!),
        ];
        procesados.forEach(notas => {
          const match = notas.match(/^\[([A-Z0-9]+)\]/);
          if (match && !tiposVistos.has(match[1])) {
            tiposVistos.add(match[1]);
            tags.push(<IncidenciaCodigo key={`proc-${match[1]}`} codigo={match[1]} tooltip={notas.replace(/^\[[A-Z0-9]+\]\s*/, '')} />);
          }
        });
        return tags.length > 0 ? tags : <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Estado',
      dataIndex: 'estadoId',
      key: 'estado',
      width: 70,
      render: (val: number, record: ReconciliacionTransmision) => {
        if (batchProcessingId === record.id) {
          return <Spin size="small" />;
        }
        const estados: Record<number, string> = {
          [ESTADO_PENDIENTE]: 'Pendiente',
          [ESTADO_REALIZADO]: 'Realizada',
          [ESTADO_DESCARTADO]: 'Descartada',
        };
        const tag = <EstadoOperacion estadoId={val} etiqueta={estados[val] || `Estado ${val}`} bloqueado={nivelDesdeIncidencias(codigosFila(record)) === 'urgente'} />;
        // En descartadas, el comentario de gestión se consulta sin abrir la ficha
        return val === ESTADO_DESCARTADO && record.notasGestion
          ? <Tooltip title={record.notasGestion}>{tag}</Tooltip>
          : tag;
      },
    },
    {
      title: 'Info',
      key: 'info',
      width: 120,
      ellipsis: true,
      render: (_, r) => {
        // En los casos E3 (operación abierta) el motor deja el texto en descripcionE3; tiene prioridad.
        const info = r.tieneIncidenciaE3 ? (r.descripcionE3 || r.infoProcesamiento) : r.infoProcesamiento;
        return info ? (
          <Tooltip title={info}><Text type="secondary" style={{ fontSize: 12 }}>{info}</Text></Tooltip>
        ) : null;
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
  };

  // Selección habilitada en vistas de un único estado (Pendiente / Descartada),
  // salvo durante un procesamiento por lotes en curso.
  const selectionEnabled = !processing && (selectedEstado === ESTADO_PENDIENTE || selectedEstado === ESTADO_DESCARTADO);

  // Opciones del selector de cambio de estado por lotes (por ahora, una única opción por vista).
  const batchTargetOptions = selectedEstado === ESTADO_PENDIENTE
    ? [{ value: ESTADO_DESCARTADO, label: <FctEstado personalizado={{ etiqueta: 'Descartada', familia: 'neutro', icono: 'cancelado' }} /> }]
    : [{ value: ESTADO_PENDIENTE, label: <FctEstado personalizado={{ etiqueta: 'Pendiente', familia: 'aviso', icono: 'hora' }} /> }];

  if (error) {
    return (
      <Result
        status="error"
        title="Error al cargar transmisiones"
        extra={<Button type="primary" onClick={loadData} icon={<ReloadOutlined />}>Reintentar</Button>}
      />
    );
  }

  const handleEstadoChange = (value: string | number) => {
    setSelectedEstado(Number(value));
    setSearchText('');
    setIncidenciaFilter('todos');
    setProvinciaFilter(PROVINCIA_TODAS);
    setSelectedRowKeys([]);
    setBatchEstado(undefined);
  };

  const handleRefresh = async () => {
    await Promise.all([loadData(), loadCounts()]);
  };

  const pctLote = batchProgress ? Math.round((batchProgress.current / batchProgress.total) * 100) : 0;

  return (
    <div>
      <style>{`
        .batch-active-row > td {
          background-color: ${fct.estado.proceso.relleno} !important;
        }
      `}</style>
      {/* Cabecera */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginBottom: 16 }}>
        <Select
          value={incidenciaFilter}
          onChange={handleIncidenciaChange}
          style={{ width: 180 }}
          size="large"
          options={[
            { label: 'Todos', value: 'todos' },
            { label: 'Sin bloqueo', value: 'sin' },
            { label: 'Con bloqueo', value: 'con' },
            { label: 'Con bloqueo E4', value: 'e4' },
          ]}
        />
        <Select
          value={provinciaFilter}
          onChange={handleProvinciaChange}
          style={{ width: 220 }}
          size="large"
          showSearch
          optionFilterProp="label"
          options={provinciaOptions}
        />
        {selectedEstado === ESTADO_PENDIENTE && incidenciaFilter === 'sin' && (
          <>
            <Button
              type="primary"
              onClick={handleProcesarTodos}
              disabled={sinIncidenciaPendientes.length === 0 || processing}
              loading={processing}
            >
              Procesar todos sin bloqueo ({sinIncidenciaPendientes.length})
            </Button>
            <Button
              onClick={handleProcesarSeleccionados}
              disabled={selectedRowKeys.length === 0 || processing}
              loading={processing}
            >
              Procesar seleccionados ({selectedRowKeys.length})
            </Button>
          </>
        )}
        {selectedEstado === ESTADO_PENDIENTE && incidenciaFilter === 'e4' && (
          <Button
            onClick={handleProcesarE4Seleccionados}
            disabled={selectedRowKeys.length === 0 || processing}
            loading={processing}
          >
            Procesar con bloqueo E4 seleccionados ({selectedRowKeys.length})
          </Button>
        )}
        <div style={{ flex: 1 }} />
        <Input
          placeholder="Buscar farmacia..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={e => handleSearch(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
        <Segmented
          value={selectedEstado}
          onChange={handleEstadoChange}
          disabled={loading || loadingCounts}
          options={[
            {
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Todos</span>
                  <Badge
                    count={estadoCounts.pendiente + estadoCounts.realizada + estadoCounts.descartada}
                    style={{ backgroundColor: fct.tinta['700'] }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: ESTADO_TODOS,
            },
            {
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Pendiente</span>
                  <Badge
                    count={estadoCounts.pendiente}
                    style={{ backgroundColor: fct.estado.aviso.linea }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: ESTADO_PENDIENTE,
            },
            {
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Realizada</span>
                  <Badge
                    count={estadoCounts.realizada}
                    style={{ backgroundColor: fct.estado.exito.linea }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: ESTADO_REALIZADO,
            },
            {
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Descartada</span>
                  <Badge
                    count={estadoCounts.descartada}
                    style={{ backgroundColor: fct.estado.neutro.linea }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: ESTADO_DESCARTADO,
            },
          ]}
          size="large"
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          loading={loading}
        >
          Actualizar
        </Button>
      </div>

      {/* Barra de cambio de estado por lotes (descartar pendientes / revertir descartadas) */}
      {selectionEnabled && selectedRowKeys.length > 0 && (
        <div style={{
          marginBottom: 12, padding: '10px 16px',
          background: fct.estado.proceso.relleno, border: `1px solid ${fct.estado.proceso.linea}`, borderRadius: fct.radio.control,
          display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <Text strong><span className="fct-mono">{selectedRowKeys.length}</span> seleccionados</Text>
          <Select
            placeholder="Marcar como..."
            value={batchEstado}
            onChange={setBatchEstado}
            style={{ width: 200 }}
            options={batchTargetOptions}
          />
          <Button disabled={batchEstado === undefined} loading={batchEstadoLoading} onClick={handleBatchEstado}>
            Aceptar
          </Button>
          <Button onClick={() => { setSelectedRowKeys([]); setBatchEstado(undefined); }}>Cancelar</Button>
        </div>
      )}

      {/* Barra de progreso del procesamiento por lotes */}
      {batchProgress && (
        <div style={{
          marginBottom: 12,
          padding: '12px 16px',
          background: batchProgress.done
            ? (batchProgress.failed > 0 ? fct.estado.aviso.relleno : fct.estado.exito.relleno)
            : fct.estado.proceso.relleno,
          border: `1px solid ${batchProgress.done ? (batchProgress.failed > 0 ? fct.estado.aviso.linea : fct.estado.exito.linea) : fct.estado.proceso.linea}`,
          borderRadius: fct.radio.control,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Space>
              {!batchProgress.done && <Spin size="small" />}
              <Text strong>
                {batchProgress.done
                  ? (batchProgress.cancelled
                    ? `Procesamiento cancelado — ${batchProgress.current - 1} de ${batchProgress.total}`
                    : `Procesamiento completado — ${batchProgress.total} transmisiones`)
                  : `Procesando ${Math.ceil(batchProgress.current)} de ${batchProgress.total}...`}
              </Text>
            </Space>
            {batchProgress.done ? (
              <Button size="small" onClick={handleCloseBatchProgress}>Cerrar</Button>
            ) : (
              <Button size="small" icon={<StopOutlined />} onClick={handleCancelBatch} danger>Cancelar</Button>
            )}
          </div>
          {/* Barra del manual (familia Proceso); el resultado lo dice el texto y el color del contenedor, no la barra */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pctLote}>
            <div className="fct-progreso__pista" style={{ flex: 1 }}><i style={{ width: `${pctLote}%` }} /></div>
            <span className="fct-mono" style={{ fontSize: 12 }}>{pctLote}%</span>
          </div>
          <div style={{ marginTop: 4, display: 'flex', gap: 16 }}>
            {!batchProgress.done && <Text type="secondary" style={{ fontSize: 12 }}>{batchProgress.currentName}</Text>}
            <Text type="success" style={{ fontSize: 12 }}><span className="fct-mono">{batchProgress.success}</span> OK</Text>
            {batchProgress.failed > 0 && <Text type="danger" style={{ fontSize: 12 }}><span className="fct-mono">{batchProgress.failed}</span> con error</Text>}
          </div>
        </div>
      )}

      {/* Tabla */}
      <Table
        dataSource={filteredData.map(t => ({ ...t, key: t.id }))}
        columns={columns}
        rowSelection={selectionEnabled ? rowSelection : undefined}
        loading={loading}
        pagination={processing ? false : { defaultPageSize: 100, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: total => `${total} registros` }}
        rowClassName={(record) => [batchProcessingId === record.id ? 'batch-active-row' : '', claseFilaAtencion(nivelDesdeIncidencias(codigosFila(record)))].filter(Boolean).join(' ')}
        onRow={record => ({
          onClick: processing ? undefined : () => openDetail(record),
          style: {
            cursor: processing ? 'default' : 'pointer',
          },
        })}
        size="middle"
        scroll={{ x: 900 }}
        locale={{ emptyText: <Empty description="No hay transmisiones pendientes" /> }}
      />

      {/* Modal de detalle */}
      <Modal
        open={modalVisible}
        onCancel={() => { setModalVisible(false); setShowFuenteOficialModal(false); }}
        footer={null}
        title={selectedItem ? <span><Tag>{selectedItem.patron}</Tag> {selectedItem.farmacia?._Name || 'Farmacia ' + selectedItem.farmaciaId}</span> : ''}
        width={showFuenteOficialModal ? '48%' : '60%'}
        style={showFuenteOficialModal ? { top: 20, margin: '0 0 0 24px' } : { top: 20 }}
        styles={{ body: { padding: '24px', background: fct.superficie.hundida } }}
      >
        {selectedItem && (
          <div>
            {/* Layout principal: Farmacia (izq) + flechas + CT/AT (der) */}
            <Row gutter={[0, 16]} style={{ marginBottom: 24 }}>

              {/* Card Farmacia */}
              <Col xs={24} lg={11}>
                <Card
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: 'gs',
                              label: <span>ID Gescom: <strong className="fct-mono">{selectedItem.farmaciaId}</strong> <CopyOutlined style={{ marginLeft: 4 }} /></span>,
                              onClick: () => { navigator.clipboard.writeText(String(selectedItem.farmaciaId)); },
                            },
                          ],
                        }}
                        trigger={['click']}
                      >
                        <span style={{ cursor: 'pointer' }}>
                          <ShopOutlined style={{ marginRight: 8 }} />
                          Farmacia
                        </span>
                      </Dropdown>
                      <Tooltip title="Ver titulares actuales en Gescom">
                        <InfoCircleOutlined
                          aria-label="Ver titulares actuales en Gescom"
                          style={{ cursor: 'pointer', fontSize: 16 }}
                          onClick={() => setShowTitularesModal(true)}
                        />
                      </Tooltip>
                    </div>
                  }
                  bordered
                  style={{
                    borderColor: fct.linea.tarjeta,
                    background: `linear-gradient(to bottom, ${fct.superficie.tarjeta} 0%, ${fct.superficie.hundida} 100%)`,
                    boxShadow: fct.elev['1'],
                    height: '100%',
                    position: 'relative',
                  }}
                  headStyle={{ backgroundColor: fct.superficie.hundida, fontWeight: 'bold' }}
                >
                  {/* Sin enlace a Gedeco: el ID ya es de Gescom, que Gedeco no conoce (#149); el enlace nuevo, en #151. */}
                  <span
                    style={{ color: 'inherit', textDecoration: 'none', fontWeight: 'bold', fontSize: 14, display: 'block' }}
                  >
                    {selectedItem.farmacia?._Name || `ID ${selectedItem.farmaciaId}`}
                  </span>
                  <div style={{
                    marginTop: 12,
                    padding: '4px 12px',
                    backgroundColor: fct.estado.neutro.relleno,
                    border: `1px solid ${fct.estado.neutro.linea}`,
                    borderRadius: fct.radio.micro,
                    fontSize: '13px',
                    color: fct.estado.neutro.texto,
                    display: 'inline-block',
                  }}>
                    {selectedItem.farmacia?.provincia || '—'}
                  </div>
                  <div style={{ position: 'absolute', bottom: 16, right: 16 }}>
                    <Tooltip
                      title={tieneCoberturaFuente(selectedItem.farmacia?.provincia)
                        ? 'Contrastar con listado de referencia en la zona'
                        : `Sin fuente oficial para ${selectedItem.farmacia?.provincia || 'esta provincia'}`}
                    >
                      <Button
                        icon={<GlobalOutlined />}
                        size="small"
                        disabled={!tieneCoberturaFuente(selectedItem.farmacia?.provincia)}
                        onClick={() => setShowFuenteOficialModal(true)}
                      >
                        Consultar fuente oficial
                      </Button>
                    </Tooltip>
                  </div>
                </Card>
              </Col>

              {/* Flechas + Cards CT/AT */}
              <Col xs={24} lg={13}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingLeft: 8 }}>

                  {/* CT: flecha + card */}
                  {selectedItem.vendedores?.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Tooltip title="Salen de la farmacia">
                        <ArrowRightOutlined style={{ fontSize: 18, flexShrink: 0 }} />
                      </Tooltip>
                      <Card
                        title={<TipoOperacion alta={false} etiqueta="Titulares salientes (CT)" />}
                        size="small"
                        bordered
                        style={{
                          flex: 1,
                          borderColor: fct.linea.tarjeta,
                          boxShadow: fct.elev['1'],
                        }}
                        headStyle={{ backgroundColor: fct.superficie.hundida, fontWeight: 'bold' }}
                      >
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          {selectedItem.vendedores.map((v, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                              <Space size="small">
                                {v.resultado != null && <ResultadoIcono ok={v.resultado === 1} />}
                                {/* Sin enlace a Gedeco: el ID ya es de Gescom, que Gedeco no conoce (#149); el enlace nuevo, en #151. */}
                                <span
                                  style={{ color: 'inherit', textDecoration: 'none' }}
                                >
                                  {v.cliente?._Name || `ID ${v.vendedorId}`}
                                </span>
                              </Space>
                              <Space size="small">
                                {v.fechaFin && <Text type="secondary" className="fct-mono" style={{ fontSize: 12 }}>{new Date(v.fechaFin).toLocaleDateString('es-ES')}</Text>}
                                {v.excepcionE2 && <IncidenciaCodigo codigo="E2" tooltip={INCIDENCIA_TOOLTIPS.E2} />}
                              </Space>
                            </div>
                          ))}
                        </Space>
                      </Card>
                    </div>
                  )}

                  {/* AT: flecha + card */}
                  {selectedItem.compradores?.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Tooltip title="Entran en la farmacia">
                        <ArrowLeftOutlined style={{ fontSize: 18, flexShrink: 0 }} />
                      </Tooltip>
                      <Card
                        title={<TipoOperacion alta etiqueta="Titulares entrantes (AT)" />}
                        size="small"
                        bordered
                        style={{
                          flex: 1,
                          borderColor: fct.linea.tarjeta,
                          boxShadow: fct.elev['1'],
                        }}
                        headStyle={{ backgroundColor: fct.superficie.hundida, fontWeight: 'bold' }}
                      >
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          {selectedItem.compradores.map((c, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                              <Space size="small">
                                {c.resultado != null && <ResultadoIcono ok={c.resultado === 1} />}
                                {/* Sin enlace a Gedeco: el ID ya es de Gescom, que Gedeco no conoce (#149); el enlace nuevo, en #151. */}
                                <span
                                  style={{ color: 'inherit', textDecoration: 'none' }}
                                >
                                  {c.cliente?._Name || `ID ${c.compradorId}`}
                                </span>
                              </Space>
                              <Space size="small">
                                {c.fechaInicio && <Text type="secondary" className="fct-mono" style={{ fontSize: 12 }}>{new Date(c.fechaInicio).toLocaleDateString('es-ES')}</Text>}
                                {c.excepcionE1 && <IncidenciaCodigo codigo="E1" tooltip={INCIDENCIA_TOOLTIPS.E1} />}
                              </Space>
                            </div>
                          ))}
                        </Space>
                      </Card>
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            {/* Comentario (izq) + Bloqueos (der) */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              {/* Comentario — alineado con card farmacia */}
              <Col xs={24} lg={11}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Comentario</Text>
                <TextArea
                  value={editNotas}
                  onChange={e => setEditNotas(e.target.value)}
                  autoSize={{ minRows: 2, maxRows: 5 }}
                  placeholder="Añadir comentario..."
                />
              </Col>

              {/* Bloqueos — alineado con cards titulares */}
              {selectedItem.tieneExcepciones && (
                <Col xs={24} lg={13}>
                  {relatedIncidencias.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingLeft: 8 }}>
                      {relatedIncidencias.map((inc) => (
                        <Card
                          key={inc.id}
                          size="small"
                          bordered
                          style={{ borderColor: inc.tipo === 'I1' ? fct.estado.neutro.linea : fct.estado.riesgo.linea, background: inc.tipo === 'I1' ? fct.estado.neutro.relleno : fct.estado.riesgo.relleno }}
                        >
                          <div style={{ display: 'flex', gap: 12 }}>
                            <span style={{ flexShrink: 0, fontSize: 14 }}><IncidenciaCodigo codigo={inc.tipo} /></span>
                            <div>
                              <Text strong style={{ display: 'block', color: inc.tipo === 'I1' ? fct.estado.neutro.texto : fct.estado.riesgo.texto }}>
                                {inc.tipo === 'E1'
                                  ? 'Alta de titularidad bloqueada porque su finalización se ha verificado recientemente'
                                  : inc.tipo === 'E2'
                                  ? 'Cierre de titularidad bloqueado porque la titularidad se ha verificado recientemente'
                                  : 'La farmacia quedaría sin titular con estos cambios'}
                              </Text>
                              {inc.descripcion && (
                                <Text style={{ display: 'block', marginTop: 4 }}>{inc.descripcion}</Text>
                              )}
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div style={{ paddingLeft: 8 }}>
                      {selectedItem.vendedores?.filter(v => v.excepcionE2).map((v, i) => (
                        <Card key={`e2-${i}`} size="small" bordered style={{ borderColor: fct.estado.riesgo.linea, background: fct.estado.riesgo.relleno, marginBottom: 8 }}>
                          <div style={{ display: 'flex', gap: 12 }}>
                            <span style={{ flexShrink: 0, fontSize: 14 }}><IncidenciaCodigo codigo="E2" /></span>
                            <Text strong style={{ color: fct.estado.riesgo.texto }}>Cierre de titularidad bloqueado porque la titularidad se ha verificado recientemente</Text>
                          </div>
                        </Card>
                      ))}
                      {selectedItem.compradores?.filter(c => c.excepcionE1).map((c, i) => (
                        <Card key={`e1-${i}`} size="small" bordered style={{ borderColor: fct.estado.riesgo.linea, background: fct.estado.riesgo.relleno, marginBottom: 8 }}>
                          <div style={{ display: 'flex', gap: 12 }}>
                            <span style={{ flexShrink: 0, fontSize: 14 }}><IncidenciaCodigo codigo="E1" /></span>
                            <Text strong style={{ color: fct.estado.riesgo.texto }}>Alta de titularidad bloqueada porque su finalización se ha verificado recientemente</Text>
                          </div>
                        </Card>
                      ))}
                      {selectedItem.tieneIncidenciaI1 && (
                        <Card key="i1" size="small" bordered style={{ borderColor: fct.estado.neutro.linea, background: fct.estado.neutro.relleno, marginBottom: 8 }}>
                          <div style={{ display: 'flex', gap: 12 }}>
                            <span style={{ flexShrink: 0, fontSize: 14 }}><IncidenciaCodigo codigo="I1" /></span>
                            <Text strong style={{ color: fct.estado.neutro.texto }}>La farmacia quedaría sin titular con estos cambios</Text>
                          </div>
                        </Card>
                      )}
                    </div>
                  )}
                </Col>
              )}
            </Row>

            {/* Panel de resultado de procesamiento */}
            {(procesarResult || selectedItem.notasProcesamiento) && (
              <Collapse
                size="small"
                defaultActiveKey={procesarResult ? ['1'] : []}
                style={{ marginBottom: 16 }}
                items={[{
                  key: '1',
                  label: <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span><ThunderboltOutlined style={{ marginRight: 8 }} />Resultado del procesamiento</span>{selectedItem.fechaProcesamiento && <Text type="secondary" className="fct-mono" style={{ fontSize: 12 }}>{new Date(selectedItem.fechaProcesamiento).toLocaleDateString('es-ES')}</Text>}</div>,
                  children: (
                    <div>
                      {/* Estado general */}
                      <div style={{ marginBottom: 8 }}>
                        {procesarResult?.tipoIncidencia && <IncidenciaCodigo codigo={procesarResult.tipoIncidencia} />}
                        <Text strong>
                          {selectedItem.notasProcesamiento || (procesarResult ? `Caso: ${procesarResult.resultadoCasoLimpio}` : '')}
                        </Text>
                      </div>

                      {/* Detalle por titular (de los datos guardados en la entidad) */}
                      {selectedItem.vendedores?.filter(v => v.resultado != null).length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Titulares salientes:</Text>
                          {selectedItem.vendedores.filter(v => v.resultado != null).map((v, i) => (
                            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 4 }}>
                              <ResultadoIcono ok={v.resultado === 1} style={{ marginTop: 4 }} />
                              <div>
                                <Text>{v.cliente?._Name || `ID ${v.vendedorId}`}</Text>
                                {v.resultadoNotas && <Text type="secondary" style={{ display: 'block', fontSize: 12, whiteSpace: 'pre-wrap' }}>{v.resultadoNotas}</Text>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {selectedItem.compradores?.filter(c => c.resultado != null).length > 0 && (
                        <div>
                          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Titulares entrantes:</Text>
                          {selectedItem.compradores.filter(c => c.resultado != null).map((c, i) => (
                            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 4 }}>
                              <ResultadoIcono ok={c.resultado === 1} style={{ marginTop: 4 }} />
                              <div>
                                <Text>{c.cliente?._Name || `ID ${c.compradorId}`}</Text>
                                {c.resultadoNotas && <Text type="secondary" style={{ display: 'block', fontSize: 12, whiteSpace: 'pre-wrap' }}>{c.resultadoNotas}</Text>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Info (notasTramitacion) */}
                      {(selectedItem.infoProcesamiento || procesarResult?.notasTramitacion) && (
                        <div style={{ marginTop: 8, padding: '8px 12px', background: fct.superficie.hundida, borderRadius: fct.radio.micro, border: `1px solid ${fct.linea.fila}` }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Info: </Text>
                          <Text style={{ fontSize: 12 }}>{selectedItem.infoProcesamiento || procesarResult?.notasTramitacion}</Text>
                        </div>
                      )}
                    </div>
                  ),
                }]}
              />
            )}

            {/* Acciones: estado selector + Procesar + Guardar */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <Select
                value={editEstadoId}
                onChange={setEditEstadoId}
                style={{ width: 160 }}
              >
                <Select.Option value={ESTADO_PENDIENTE}><FctEstado personalizado={{ etiqueta: 'Pendiente', familia: 'aviso', icono: 'hora' }} /></Select.Option>
                <Select.Option value={ESTADO_REALIZADO}><FctEstado personalizado={{ etiqueta: 'Realizada', familia: 'exito' }} /></Select.Option>
                <Select.Option value={ESTADO_DESCARTADO}><FctEstado personalizado={{ etiqueta: 'Descartada', familia: 'neutro', icono: 'cancelado' }} /></Select.Option>
              </Select>

              <Button
                icon={<SaveOutlined />}
                onClick={() => handleUpdateEstado(selectedItem.id, editEstadoId, editNotas || undefined)}
              >
                Guardar
              </Button>

              {selectedItem.estadoId === ESTADO_PENDIENTE && (
                <Tooltip title={selectedItem.tieneExcepciones ? 'Los casos con bloqueo requieren revisión antes de procesar' : undefined}>
                <Button
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  loading={procesando}
                  disabled={selectedItem.tieneExcepciones}
                  onClick={() => {
                    Modal.confirm({
                      title: 'Actualizar Gescom',
                      content: 'Se actualizarán las titularidades en Gescom (titulares entrantes y salientes).',
                      okText: 'Aceptar',
                      cancelText: 'Cancelar',
                      onOk: async () => {
                        setProcesando(true);
                        try {
                          const result = await procesarTransmision(selectedItem.id);
                          setProcesarResult(result);

                          if (result.error !== 0) {
                            message.error(`Error: ${result.mensajeError}`);
                          } else if (result.resultadoCasoLimpio !== 'limpio') {
                            // El motivo de cada titular que no se pudo procesar.
                            const motivos = [...result.resultadosSalientes, ...result.resultadosEntrantes]
                              .filter(r => !r.procesado).map(r => r.motivo).join(' ');
                            message.warning(`Caso no limpio${result.tipoIncidencia ? ` [${result.tipoIncidencia}]` : ''}: ${motivos}`);
                          } else {
                            message.success('Transmisión procesada correctamente');
                            setEditEstadoId(ESTADO_REALIZADO);
                          }

                          const refreshed = await getRecTransmisionById(selectedItem.id);
                          setSelectedItem(refreshed);
                          setEditEstadoId(refreshed.estadoId);
                        } catch (err: any) {
                          // Un rechazo de Gescom (#189) llega como 409 con su motivo, sin escribir nada.
                          message.error(err?.response?.data?.message || 'Error al procesar en Gescom');
                        } finally {
                          setProcesando(false);
                        }
                      },
                    });
                  }}
                >
                  Procesar...
                </Button>
                </Tooltip>
              )}

              <div style={{ marginLeft: 'auto' }}>
                <AbrirCasoButton
                  prefill={{
                    entidadTipo: 'Transmision',
                    entidadRefId: selectedItem.id,
                    reconciliacionId: selectedItem.reconciliacionId,
                    titulo: `Revisión transmisión #${selectedItem.id}`,
                    descripcion:
                      `Farmacia: ${selectedItem.farmacia?._Name ?? '—'}\n` +
                      `id_farmacia (CU2): ${selectedItem.farmacia?.idFarmaciaCU2 ?? '—'}\n` +
                      `id_farmacia_gs (Gescom): ${selectedItem.farmaciaId}`,
                  }}
                />
              </div>
            </div>
          </div>
        )}
        {/* Titulares o empleados actuales en Gescom: dentro del detalle, para que Ant Design la ponga siempre
            por encima de él, aunque el detalle se monte después (#209). */}
        {selectedItem && (
          <FarmaciaTitularesModal
            visible={showTitularesModal}
            onClose={() => setShowTitularesModal(false)}
            farmaciaId={selectedItem.farmaciaId}
            farmaciaName={selectedItem.farmacia?._Name || 'Sin datos'}
            userId={1}
            cargarTitulares={getRecTitularesFarmaciaGescom}
          />
        )}
      </Modal>

      {/* Modal fuente oficial (GDO-T40) */}
      <FuenteOficialModal
        visible={showFuenteOficialModal}
        onClose={() => setShowFuenteOficialModal(false)}
        transmision={selectedItem}
      />
    </div>
  );
};

export default TransmisionesTab;
